import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { NucToast } from "src/reusable/components/Toast";
import { whereFilterListToJsonArray } from "src/helpers/helperService";
import { SCREENING_KIND } from "../llm/llmUtils";

// An llm template's config carries no model version (its screening persona
// owns the model) and the backend 422s if one is sent, so the param is
// dropped for that kind whatever the caller held.
const modelVersionParam = (modelVersionId, screeningKind) =>
  screeningKind === SCREENING_KIND.LLM
    ? undefined
    : (modelVersionId ?? undefined);

const getCAPAutomationConfigById = (acId) => {
  return axios
    .get(nuclearn_api_url + "/products/cap-screening-automation/", {
      params: {
        automation_config_id: acId,
      },
    })
    .then((response) => {
      return response.data;
    });
};

// The batched reads cache under their own keys, so rule edits must clear them too.
const invalidateRuleBatches = (queryClient) => {
  queryClient.invalidateQueries("modelLabelClassRulesByIds");
  queryClient.invalidateQueries("modelLabelRulesByIds");
};

const _cacheModelLabelClassRule = (modelLabelClassRule, queryClient) => {
  let { model_label_rules } = modelLabelClassRule;
  // we don't want to store the entire model_label_rule set, just the ids
  // so we will create a new model_label_rules attribute
  let new_model_label_rules = {};
  // loop over model_label_rules and cache the data
  Object.keys(model_label_rules).forEach((output_value) => {
    let model_label_rule = model_label_rules[output_value];
    queryClient.setQueryData(
      ["modelLabelRuleById", model_label_rule.id],
      model_label_rule,
    );
    new_model_label_rules[output_value] = { id: model_label_rule.id };
  });
  modelLabelClassRule.model_label_rules = new_model_label_rules;
  queryClient.setQueryData(
    ["modelLabelClassRuleById", modelLabelClassRule.id],
    modelLabelClassRule,
  );
  return modelLabelClassRule;
};

const fetchAutomationConfigById = (acId, queryClient) =>
  getCAPAutomationConfigById(acId).then((data) => {
    // It is useful to cache all of the model label class rules
    // and model label rules, so they aren't each fetched individually
    let { model_label_class_rules } = data;
    // we don't want to store the entire model_label_class_rule set, just the ids
    // so we will create a new model_label_class_rules attribute
    let new_model_label_class_rules = {};
    // loop over model_label_class_rules object and cache the data
    Object.keys(model_label_class_rules).forEach((model_label_class_name) => {
      let modelLabelClassRule = model_label_class_rules[model_label_class_name];
      _cacheModelLabelClassRule(modelLabelClassRule, queryClient);
      new_model_label_class_rules[model_label_class_name] = {
        id: modelLabelClassRule.id,
      };
    });
    data.model_label_class_rules = new_model_label_class_rules;
    return data;
  });

export const useAutomationConfigByID = (acId) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["automationConfigById", acId],
    () => fetchAutomationConfigById(acId, queryClient),
    {
      enabled: acId !== null && acId !== undefined,
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * Loads several automation configs, sharing the cache entries of
 * `useAutomationConfigByID`. Returns one query result per id, in order.
 */
export const useAutomationConfigsByIds = (acIds) => {
  const queryClient = useQueryClient();
  return useQueries(
    acIds.map((acId) => ({
      queryKey: ["automationConfigById", acId],
      queryFn: () => fetchAutomationConfigById(acId, queryClient),
      enabled: acId !== null && acId !== undefined,
      refetchOnWindowFocus: false,
    })),
  );
};

const getCAPAutomationConfigByName = (acName) => {
  return axios
    .get(nuclearn_api_url + "/products/cap-screening-automation/", {
      params: { automation_config_name: acName },
    })
    .then((response) => {
      return response.data;
    });
};

// eslint-disable-next-line no-unused-vars
const useAutomationConfigByName = (acName) => {
  return useQuery(
    ["automationConfigByName", acName],
    () => getCAPAutomationConfigByName(acName),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getAutomationConfigDataById = (automationConfigId, testRunId) => {
  return axios
    .get(nuclearn_api_url + "/products/cap-screening-automation/data", {
      params: {
        automation_config_id: automationConfigId,
        test_run_id: testRunId,
      },
    })
    .then((response) => {
      return response.data;
    });
};

export const useAutomationConfigDataById = (automationConfigId, testRunId) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["automationConfigDataById", automationConfigId],
    () => {
      return getAutomationConfigDataById(automationConfigId, testRunId);
    },
    {
      enabled: false, // only allow manual fetching on this query
      //refetchOnWindowFocus: false,
      retry: 1,
      onSuccess: () => {
        queryClient.invalidateQueries([
          "automationConfigById",
          automationConfigId,
        ]);
      },
    },
  );
};

const getAutomationConfigTemplate = (automationConfigTemplateId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/template/" +
        automationConfigTemplateId,
    )
    .then((res) => res.data);
};

export const useAutomationConfigTemplate = (automationConfigTemplateId) => {
  return useQuery(
    ["automationConfigTemplate", automationConfigTemplateId],
    () => getAutomationConfigTemplate(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const createAutomationConfigTemplate = (act) => {
  return axios
    .put(nuclearn_api_url + "/products/cap-screening-automation/template/", act)
    .then((response) => {
      return response.data;
    });
};

export const useCreateAutomationConfigTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createAutomationConfigTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("allAutomationConfigTemplate");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const updateAutomationConfigTemplate = (automationConfigTemplate) => {
  return axios
    .patch(
      nuclearn_api_url +
        "/products/cap-screening-automation/template/" +
        automationConfigTemplate.id,
      automationConfigTemplate,
    )
    .then((response) => {
      return response.data;
    });
};

export const useUpdateAutomationConfigTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateAutomationConfigTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("allAutomationConfigTemplate");
      queryClient.invalidateQueries("automationConfigTemplate");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteAutomationConfigTemplate = (automationConfigTemplateId) => {
  return axios
    .delete(
      nuclearn_api_url +
        "/products/cap-screening-automation/template/" +
        automationConfigTemplateId,
    )
    .then(() => {
      //console.log(response);
      return automationConfigTemplateId;
    });
};

export const useDeleteAutomationConfigTemplate = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteAutomationConfigTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries("allAutomationConfigTemplate");
      queryClient.invalidateQueries("automationConfigTemplate");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getAllAutomationConfigTemplates = (
  parentAutomationConfigTemplateId,
  embedChildren,
  productFk,
) => {
  let params = {};
  if (parentAutomationConfigTemplateId) {
    params.parent_automation_config_template_id =
      parentAutomationConfigTemplateId;
  }
  if (embedChildren !== null) {
    params.embed_children = embedChildren;
  }
  if (productFk !== null) {
    params.product_fk = productFk;
  }
  return axios
    .get(
      nuclearn_api_url + "/products/cap-screening-automation/all_templates/",
      {
        params: params,
      },
    )
    .then((res) => res.data);
};

export const useAllAutomationConfigTemplates = (
  parentAutomationConfigTemplateId,
  embedChildren,
  productFk,
) => {
  return useQuery(
    [
      "allAutomationConfigTemplate",
      parentAutomationConfigTemplateId,
      embedChildren,
      productFk,
    ],
    () =>
      getAllAutomationConfigTemplates(
        parentAutomationConfigTemplateId,
        embedChildren,
        productFk,
      ),
    {
      refetchOnWindowFocus: false,
    },
  );
};

const getAllAutomationConfigs = (automationConfigTemplateId) => {
  return axios
    .get(nuclearn_api_url + "/products/cap-screening-automation/all", {
      params: { automation_config_template_id: automationConfigTemplateId },
    })
    .then((res) => res.data);
};

export const useAllAutomationConfigs = (
  automationConfigTemplateId,
  onSuccess,
) => {
  return useQuery(
    ["allAutomationConfigs", automationConfigTemplateId],
    () => getAllAutomationConfigs(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
      onSuccess: onSuccess,
    },
  );
};

const promoteAutomationConfig = (id) => {
  return axios.patch(
    nuclearn_api_url +
      "/products/cap-screening-automation/promote-automation-config",
    {
      automation_config_id: id,
    },
  );
};

export const usePromoteAutomationConfig = (automationConfigId) => {
  const queryClient = useQueryClient();

  return useMutation(() => promoteAutomationConfig(automationConfigId), {
    onSuccess: () => {
      queryClient.invalidateQueries([
        "automationConfigById",
        automationConfigId,
      ]);
      queryClient.invalidateQueries("allAutomationConfigs");
    },
  });
};

export const useArchiveAutomationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const { automationConfigId } = vars;
      return axios.delete(
        nuclearn_api_url +
          "/products/cap-screening-automation/archive-automation-config",
        {
          params: {
            automation_config_id: automationConfigId,
          },
        },
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("allAutomationConfigs");
      },
    },
  );
};

// eslint-disable-next-line no-unused-vars
const useUpdateAutomationTemplateStatus = (automationTemplateStatus) => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const { automationTemplateId } = vars;

      if (automationTemplateStatus === "CURRENT") {
        return axios.patch(
          nuclearn_api_url +
            "/products/cap-screening-automation/promote-automation-config",
          {
            automation_config_id: automationTemplateId,
          },
        );
      }

      if (automationTemplateStatus === "ARCHIVE") {
        return axios.delete(
          nuclearn_api_url +
            "/products/cap-screening-automation/archive-automation-config",
          {
            automation_config_id: automationTemplateId,
          },
        );
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("allAutomationConfigs");
      },
    },
  );
};

const putNewAutomationConfig = ({
  automationConfigTemplateId,
  modelVersionId,
  name,
  screeningKind,
}) => {
  return axios.put(
    nuclearn_api_url + "/products/cap-screening-automation/",
    null,
    {
      params: {
        automation_config_template_id: automationConfigTemplateId,
        model_version_id: modelVersionParam(modelVersionId, screeningKind),
        name: !name ? "" : name,
      },
    },
  );
};

export const useNewAutomationConfig = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(putNewAutomationConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries("allAutomationConfigs");
    },
  });
  return mutation;
};

// export const patchAutomationConfig = (automationConfig) => {
//   return axios.patch(nuclearn_api_url + "/products/cap-screening-automation/", {
//     ...automationConfig,
//   });
// };

export const patchAutomationConfigName = (
  existingAutomationConfigId,
  automationConfigName,
) => {
  return axios.patch(
    `${nuclearn_api_url}/products/cap-screening-automation/update-automation-config-name`,
    {
      id: existingAutomationConfigId,
      name: automationConfigName,
    },
  );
};

export const useUpdateAutomationConfigName = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const { existingAutomationConfigId, newAutomationConfigName } = vars;
      return patchAutomationConfigName(
        existingAutomationConfigId,
        newAutomationConfigName,
      );
    },
    {
      onSuccess: (_, variables) => {
        const { existingAutomationConfigId, newAutomationConfigName } =
          variables;
        queryClient.getQueryData([
          "automationConfigById",
          existingAutomationConfigId,
        ]);
        queryClient.setQueryData(
          ["automationConfigById", existingAutomationConfigId],
          (old_at) => {
            old_at.name = newAutomationConfigName;
            return old_at;
          },
        );
        queryClient.invalidateQueries([
          "automationConfigById",
          existingAutomationConfigId,
        ]);
      },
    },
  );
};

export const patchUpdateAutomationConfigDetails = ({
  automationConfigId,
  manualSampleRate,
  prohibitDuplicateAutomation,
}) => {
  return axios.patch(
    `${nuclearn_api_url}/products/cap-screening-automation/update-automation-config-details`,
    {
      id: automationConfigId,
      manual: { sample_rate: manualSampleRate },
      prohibit_duplicate_automation: prohibitDuplicateAutomation,
    },
  );
};

export const useUpdateAutomationConfigDetails = () => {
  const queryClient = useQueryClient();
  return useMutation(
    // The mutation to run (should return a Promise)
    (automationConfigDetails) => {
      return patchUpdateAutomationConfigDetails(automationConfigDetails);
    },
    {
      // The mutation to run on mutation success
      onMutate: async (automationConfigDetails) => {
        const automationConfigKey = [
          "automationConfigById",
          automationConfigDetails.automationConfigId,
        ];
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(automationConfigKey);
        queryClient.resetQueries([
          "automationConfigDataById",
          automationConfigDetails.automationConfigId,
        ]);
        // Snapshot the previous value
        let prevAutomationConfig =
          queryClient.getQueryData(automationConfigKey);
        prevAutomationConfig.manual.sample_rate =
          automationConfigDetails.manualSampleRate;
        prevAutomationConfig.prohibit_duplicate_automation =
          automationConfigDetails.prohibitDuplicateAutomation;
        // Optimistically update to the new value
        queryClient.setQueryData(automationConfigKey, prevAutomationConfig);
        // Return a context object with the snapshotted value
        return { prevAutomationConfig, automationConfigKey };
      },
      // On failure, roll back to the previous value
      onError: (err, automationConfigDetails, context) => {
        queryClient.setQueryData(
          context.automationConfigKey,
          context.prevAutomationConfig,
        );
      },
      // After success or failure, refetch the modelLabelClass
      onSuccess: (success, automationConfigDetails, context) => {
        queryClient.invalidateQueries(context.automationConfigKey);
      },
    },
  );
};

const getModelLabelClassRuleById = (modelLabelClassRuleId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/cap-screening-automation/model-label-class-rule/${modelLabelClassRuleId}`,
    )
    .then((res) => res.data);
};

export const useGetModelLabelClassRuleById = (
  modelLabelClassRuleId,
  refetchOnMount,
) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["modelLabelClassRuleById", modelLabelClassRuleId],
    () =>
      getModelLabelClassRuleById(modelLabelClassRuleId).then((data) => {
        return _cacheModelLabelClassRule(data, queryClient);
      }),
    {
      enabled: !!modelLabelClassRuleId,
      refetchOnMount: !!refetchOnMount,
      refetchOnWindowFocus: false,
    },
  );
};

const patchModelLabelClassRule = (modelLabelClassRule) => {
  return axios.patch(
    `${nuclearn_api_url}/products/cap-screening-automation/model-label-class-rule/${modelLabelClassRule.id}`,
    modelLabelClassRule,
  );
};

const patchModelLabelClassLabelType = (modelLabelClassId, labelType) => {
  return axios.patch(
    `${nuclearn_api_url}/products/cap-screening-automation/model-label-class/${modelLabelClassId}/label-type`,
    { label_type: labelType },
  );
};

export const usePatchModelLabelClassLabelType = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ modelLabelClassId, labelType }) => {
      return patchModelLabelClassLabelType(modelLabelClassId, labelType);
    },
    {
      onSuccess: (response) => {
        if (response.data?.warning) {
          NucToast({
            message: response.data.warning,
            type: "warning",
            className: "label-type-change-warning-toast",
          });
        }
      },
      onSettled: () => {
        // Always refetch server truth on success or failure to keep UI in sync
        queryClient.invalidateQueries("modelLabelClassRuleById");
        invalidateRuleBatches(queryClient);
      },
    },
  );
};

export const usePatchModelLabelClassRule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    // The mutation to run (should return a Promise)
    (modelLabelClassRule) => {
      return patchModelLabelClassRule(modelLabelClassRule);
    },
    {
      // The mutation to run on mutation success
      onMutate: async (modelLabelClassRule) => {
        const modelLabelClassRuleQueryKey = [
          "modelLabelClassRuleById",
          modelLabelClassRule.id,
        ];
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(modelLabelClassRuleQueryKey);
        // Snapshot the previous value
        const prevModelLabelClassRule = queryClient.getQueryData(
          modelLabelClassRuleQueryKey,
        );
        // Remove the data stats
        queryClient.resetQueries([
          "automationConfigDataById",
          prevModelLabelClassRule?.automation_config_fk,
        ]);

        // Optimistically update to the new value
        queryClient.setQueryData(modelLabelClassRuleQueryKey, {
          ...prevModelLabelClassRule,
          ...modelLabelClassRule,
        });
        // Return a context object with the snapshotted value
        return { prevModelLabelClassRule, modelLabelClassRuleQueryKey };
      },
      // On failure, roll back to the previous value
      onError: (err, modelLabelClassRule, context) => {
        queryClient.setQueryData(
          context.modelLabelClassRuleQueryKey,
          context.prevModelLabelClassRule,
        );
      },
      // After success or failure, refetch the modelLabelClass
      onSuccess: (success) => {
        _cacheModelLabelClassRule(success.data, queryClient);
        invalidateRuleBatches(queryClient);
      },
    },
  );
};

// Matches MAX_RULE_IDS_PER_REQUEST in MRE's sc_auto routes; larger sets are split.
const MAX_RULE_IDS_PER_REQUEST = 500;

const getRulesByIds = (path, ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_RULE_IDS_PER_REQUEST) {
    chunks.push(ids.slice(i, i + MAX_RULE_IDS_PER_REQUEST));
  }
  return Promise.all(
    chunks.map((chunk) => {
      const params = new URLSearchParams();
      chunk.forEach((id) => params.append("ids", id));
      return axios
        .get(`${nuclearn_api_url}/products/cap-screening-automation/${path}`, {
          params,
        })
        .then((res) => res.data);
    }),
  ).then((pages) =>
    Object.fromEntries(pages.flat().map((rule) => [rule.id, rule])),
  );
};

const uniqueSortedIds = (ids) =>
  [...new Set(ids.filter((id) => id !== null && id !== undefined))].sort(
    (a, b) => a - b,
  );

const useRulesByIds = (cacheKey, path, rawIds) => {
  const ids = uniqueSortedIds(rawIds);
  return useQuery([cacheKey, ids], () => getRulesByIds(path, ids), {
    enabled: ids.length > 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Batched read of model label class rules. `data` maps id to rule; each rule
 * carries `model_label_rule_ids` (label value to label rule id), not the label
 * rules themselves. Ids the server doesn't know are absent from the map.
 */
export const useModelLabelClassRulesByIds = (modelLabelClassRuleIds) =>
  useRulesByIds(
    "modelLabelClassRulesByIds",
    "model-label-class-rules",
    modelLabelClassRuleIds,
  );

/**
 * Batched read of model label rules. `data` maps id to rule; ids the server
 * doesn't know are absent from the map.
 */
export const useModelLabelRulesByIds = (modelLabelRuleIds) =>
  useRulesByIds("modelLabelRulesByIds", "model-label-rules", modelLabelRuleIds);

const getModelLabelRuleById = (modelLabelRuleId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/cap-screening-automation/model-label-rule/${modelLabelRuleId}`,
    )
    .then((res) => res.data);
};

export const useGetModelLabelRuleById = (modelLabelRuleId, refetchOnMount) => {
  return useQuery(
    ["modelLabelRuleById", modelLabelRuleId],
    () => getModelLabelRuleById(modelLabelRuleId),
    {
      enabled: !!modelLabelRuleId,
      refetchOnMount: !!refetchOnMount,
      refetchOnWindowFocus: false,
    },
  );
};

const patchModelLabelRule = (modelLabelRule) => {
  return axios.patch(
    `${nuclearn_api_url}/products/cap-screening-automation/model-label-rule/${modelLabelRule.id}`,
    modelLabelRule,
  );
};

export const usePatchModelLabelRule = () => {
  const queryClient = useQueryClient();
  return useMutation(
    // The mutation to run (should return a Promise)
    (modelLabelRule) => {
      return patchModelLabelRule(modelLabelRule);
    },
    {
      // The mutation to run on mutation success
      onMutate: async (modelLabelRule) => {
        const modelLabelRuleQueryKey = [
          "modelLabelRuleById",
          modelLabelRule.id,
        ];
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(modelLabelRuleQueryKey);
        // Snapshot the previous value
        const prevModelLabelRule = queryClient.getQueryData(
          modelLabelRuleQueryKey,
        );
        // Remove the data stats
        // We don't actually have the automationConfigId here, so pull the parent modelLabelClassRule from the queryCache
        // and use that to get the automationConfigId
        const modelLabelClassRule = queryClient.getQueryData([
          "modelLabelClassRuleById",
          prevModelLabelRule?.model_label_class_rule_fk,
        ]);
        if (modelLabelClassRule) {
          queryClient.resetQueries([
            "automationConfigDataById",
            modelLabelClassRule?.automation_config_fk,
          ]);
        } else {
          // This shouldn't happen, but if it does, lets invalidate all automationConfigData queries
          queryClient.invalidateQueries("automationConfigDataById");
        }
        // Optimistically update to the new value
        queryClient.setQueryData(modelLabelRuleQueryKey, {
          ...prevModelLabelRule,
          ...modelLabelRule,
        });
        // Return a context object with the snapshotted value
        return { prevModelLabelRule, modelLabelRuleQueryKey };
      },
      // On failure, roll back to the previous value
      onError: (err, modelLabelRule, context) => {
        queryClient.setQueryData(
          context.modelLabelRuleQueryKey,
          context.prevModelLabelRule,
        );
      },
      // Success response includes the updated modelLabelRule
      onSuccess: (success, modelLabelRule, context) => {
        queryClient.setQueryData(context.modelLabelRuleQueryKey, success.data);
        invalidateRuleBatches(queryClient);
      },
    },
  );
};

// export const useUpdateAutomationConfig = () => {
//   const queryClient = useQueryClient();

//   const mutation = useMutation(patchAutomationConfig, {
//     onSuccess: () => {
//       queryClient.invalidateQueries("allAutomationConfigs");
//     },
//   });
//   return mutation;
// };

export const getTestRunByAutomationConfigTemplateId = (
  automationConfigTemplateId,
) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/cap-screening-automation/testrun?automation_template_id=${automationConfigTemplateId}`,
    )
    .then((response) => {
      return response.data;
    });
};
export const useGetTestRunByAutomationConfigTemplateId = (
  automationConfigTemplateId,
) => {
  return useQuery(
    ["testrun", automationConfigTemplateId],
    () => getTestRunByAutomationConfigTemplateId(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
      enabled: !!automationConfigTemplateId,
    },
  );
};

export const createTestRun = async ({
  name,
  automationConfigTemplateId,
  modelVersionId,
  datasetId,
  whereFilters = [],
  testRunId,
}) => {
  const whereFilterList = whereFilterListToJsonArray(whereFilters);
  try {
    const response = await axios.post(
      `${nuclearn_api_url}/products/cap-screening-automation/testrun`,
      null,
      {
        params: {
          name: name,
          automation_config_template_id: automationConfigTemplateId,
          model_version_id: modelVersionId,
          dataset_id: datasetId,
          filt: JSON.stringify(whereFilterList),
          test_run_id: testRunId === -1 ? null : testRunId,
        },
      },
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw error;
    }
  }
};

export const useCreateTestRun = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(createTestRun, {
    onSuccess: () => {
      queryClient.invalidateQueries("testrun");
    },
  });
  return mutation;
};

export const createAutoflowRun = (automationConfigTemplateId, datasetId) => {
  return axios.post(
    `${nuclearn_api_url}/products/cap-screening-automation/autoflowrun`,
    null,
    {
      params: {
        dataset_id: datasetId,
        automation_config_template_id: automationConfigTemplateId,
      },
    },
  );
};

export const useCreateAutoFlowRun = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ automationConfigTemplateId, datasetId }) =>
      createAutoflowRun(automationConfigTemplateId, datasetId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("autoflowRunStatus");
      },
      onError: (e) => {
        throw new Error(e.message + " " + e?.response?.data?.detail);
      },
    },
  );
};

const getAutoflowRunStatus = (automationConfigTemplateId, datasetId) => {
  return axios
    .get(
      nuclearn_api_url +
        "/products/cap-screening-automation/autoflowrun/status",
      {
        params: {
          automation_config_template_id: automationConfigTemplateId,
          dataset_id: datasetId,
        },
      },
    )
    .then((res) => res.data);
};

function shouldNotRetry404(attemptNumber, error) {
  // Return false if the requesasdft returned a 404 status code
  if (error.response && error.response.status === 404) {
    return false;
  }

  // Otherwise, use the default retry behavior
  return true;
}

export const useAutoflowRunStatus = (automationConfigTemplateId, datasetId) => {
  return useQuery(
    ["autoflowRunStatus", [automationConfigTemplateId, datasetId]],
    () => getAutoflowRunStatus(automationConfigTemplateId, datasetId),
    {
      refetchOnWindowFocus: false,
      enabled: !!automationConfigTemplateId && !!datasetId,
      retry: shouldNotRetry404,
      refetchInterval: (data) =>
        !data || data.pct_complete < 1 ? 2500 : undefined,
    },
  );
};

const getAutoflowDatasetRunnability = (automationConfigTemplateId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/cap-screening-automation/template/${automationConfigTemplateId}/autoflow-dataset-runnability`,
    )
    .then((res) => res.data);
};

/**
 * Candidate datasets for a manual AutoFlow run, each with `runnable` and the
 * `issues` explaining why not. Replaces filtering the picker down to datasets
 * tied to the promoted model version — a classical-path rule an LLM screening
 * template never applies, which left the dropdown empty and unexplained.
 */
export const useAutoflowDatasetRunnability = (automationConfigTemplateId) => {
  return useQuery(
    ["autoflowDatasetRunnability", automationConfigTemplateId],
    () => getAutoflowDatasetRunnability(automationConfigTemplateId),
    {
      refetchOnWindowFocus: false,
      enabled: !!automationConfigTemplateId,
      retry: shouldNotRetry404,
    },
  );
};

const getAutomationConfigExport = (automationConfigId) => {
  return axios
    .get(
      nuclearn_api_url + "/products/cap-screening-automation/config-export",
      {
        params: { automation_config_id: automationConfigId },
      },
    )
    .then((res) => res.data);
};

export const downloadAutomationConfigJson = (automationConfigId) => {
  return getAutomationConfigExport(automationConfigId).then((data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const aTag = document.createElement("a");
    // Name the file after the template and config so the user can tell
    // downloads apart, e.g. "CR Screening - my config.autoflow-config.json"
    const stem =
      [data.template_name, data.config_name]
        .filter(Boolean)
        .join(" - ")
        .replace(/[\\/:*?"<>|]/g, "_") || "autoflow-config";
    aTag.download = `${stem}.autoflow-config.json`;
    aTag.href = url;
    document.body.appendChild(aTag);
    aTag.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(aTag);
  });
};

const postAutomationConfigImport = ({
  automationConfigTemplateId,
  modelVersionId,
  name,
  configJson,
  screeningKind,
}) => {
  return axios
    .post(
      nuclearn_api_url + "/products/cap-screening-automation/config-import",
      configJson,
      {
        params: {
          automation_config_template_id: automationConfigTemplateId,
          model_version_id: modelVersionParam(modelVersionId, screeningKind),
          name: name ?? undefined,
        },
      },
    )
    .then((res) => res.data);
};

export const useImportAutomationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(postAutomationConfigImport, {
    onSuccess: () => {
      queryClient.invalidateQueries("allAutomationConfigs");
    },
  });
};

const patchLlmScreeningConfig = ({
  automationConfigId,
  llmScreeningConfig,
  screeningPersonaId,
}) => {
  // screeningPersonaId is a query param, not part of the config body: the body
  // is the screening contract (fields, labels, datasets) and the persona link
  // lives on the automation config row itself. -1 means "unlink"; undefined
  // means "leave the current link alone".
  const params =
    screeningPersonaId === undefined
      ? undefined
      : { screening_persona_id: screeningPersonaId };
  return axios
    .patch(
      nuclearn_api_url +
        `/products/cap-screening-automation/automation-config/${automationConfigId}/llm-screening-config`,
      llmScreeningConfig,
      { params },
    )
    .then((res) => res.data);
};

export const useUpdateLlmScreeningConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(patchLlmScreeningConfig, {
    onSuccess: (data, { automationConfigId }) => {
      // Saving predicted fields materializes label classes/rules server-side,
      // so refetch the whole config (which re-caches the rule queries too)
      queryClient.invalidateQueries([
        "automationConfigById",
        automationConfigId,
      ]);
      NucToast({
        message: "LLM screening settings saved",
        className: "llm-screening-config-save-success-toast",
        type: "success",
      });
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail;
      NucToast({
        message: `LLM screening settings NOT saved${
          typeof detail === "string" ? `: ${detail}` : ""
        }`,
        className: "llm-screening-config-save-error-toast",
        type: "error",
      });
    },
  });
};

const deleteLlmScreeningConfig = ({ automationConfigId }) => {
  return axios
    .delete(
      nuclearn_api_url +
        `/products/cap-screening-automation/automation-config/${automationConfigId}/llm-screening-config`,
    )
    .then((res) => res.data);
};

export const useDeleteLlmScreeningConfig = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteLlmScreeningConfig, {
    onSuccess: (data, { automationConfigId }) => {
      queryClient.invalidateQueries([
        "automationConfigById",
        automationConfigId,
      ]);
    },
  });
};
